/**
 * @file NumberPagination.js
 * @module src/js/components/NumberPagination
 * @description
 * **A reusable pagination component for navigating through pages of items.**
 * - Displays numbered page buttons.
 * - Highlights the currently selected page.
 * - Allows users to switch pages by clicking on page numbers.
 * - Supports navigation through large sets of pages with increment/decrement controls.
 * - Smoothly scrolls to the header section upon page change.
 *
 * @version
 * <p>
 *  2025-11-29: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Use `useMemo` and `useCallback` for optimization. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback, useMemo, useEffect } from "react";
import { useTheme } from "../contexts/ThemeProvider";

export default function NumberPagination({
    length,
    numPageButtons,
    numItemsPerPage,
    currentPage,
    setCurrentPage,
    currentFirstPage,
    setCurrentFirstPage,
    currentCategory,
    headerRef
}) {
    const { theme } = useTheme();
    
    const numPages = useMemo(() => Math.ceil(length / numItemsPerPage), [length, numItemsPerPage]);

    const handleClickPageNumber = useCallback((e) => {
        if (e.target.classList.contains('disabled')) return;
        setCurrentPage(parseInt(e.target.innerHTML));
        [100, 300].forEach((delay) => {
            setTimeout(() => headerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), delay);
        });
    }, [setCurrentPage, headerRef]);

    const handleClickPageNumInc = useCallback((e) => {
        if (e.target.classList.contains('disabled')) return;
        setCurrentFirstPage(prev => prev-numPageButtons);
    }, [numPageButtons, setCurrentFirstPage]);

    const handleClickPageNumDec = useCallback((e) => {
        if (e.target.classList.contains('disabled')) return;
        setCurrentFirstPage(prev => prev+numPageButtons);
    }, [numPageButtons, setCurrentFirstPage]);

    useEffect(() => {
        if (currentCategory) {
            setCurrentPage(1);
            setCurrentFirstPage(1);
        }
    }, [currentCategory, setCurrentPage, setCurrentFirstPage]);

    const buttonStyle = useMemo(() => ({
        color: theme === 'dark' ? 'white' : 'black',
        borderRadius: '5px'
    }), [theme]);

    return (
        <div className="center">
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div className="pagination" style={{
                    overflowX: 'auto',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    scrollbarWidth: 'thin',
                    WebkitOverflowScrolling: 'touch'
                }}>
                    <div
                        className={(currentFirstPage > 1) ? '' : 'disabled'}
                        style={buttonStyle}
                        onClick={handleClickPageNumInc}>&laquo;</div>
                    {[...Array(numPageButtons).keys()].map((item, i) => {
                        const pageNumber = currentFirstPage + item;
                        return (
                            <div 
                                key={i}
                                className={pageNumber === currentPage ? 'active' : (numPages >= pageNumber ? '' : 'disabled')}
                                onClick={handleClickPageNumber}
                                style={buttonStyle}
                            >{pageNumber}</div>
                        );
                    })}
                    <div
                        className={numPages >= currentFirstPage+numPageButtons ? '' : 'disabled'}
                        style={buttonStyle}
                        onClick={handleClickPageNumDec}>&raquo;</div>
                </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: '1em' }}>Page {currentPage} of {numPages}</div>
        </div>
    );
}
